import { BigNumber } from '@ethersproject/bignumber'
import { BaseProvider } from '@ethersproject/providers'
import {
  CallMultipleFunctionsOnSameContractParams,
  CallSameFunctionOnContractWithMultipleParams,
  CallSameFunctionOnMultipleContractsParams,
  IMulticallProvider,
  Result,
  UniswapMulticallConfig,
} from '@uniswap/smart-order-router'
import { MULTICALL_ADDRESS } from 'constants/addresses'
import { SupportedChainId } from 'constants/chains'
import _ from 'lodash'
import stats from 'stats-lite'
import { UniswapInterfaceMulticall, UniswapInterfaceMulticall__factory } from 'types/v3'

export class UniswapMulticallProvider extends IMulticallProvider<UniswapMulticallConfig> {
  private multicallContract: UniswapInterfaceMulticall

  constructor(
    protected chainId: SupportedChainId,
    protected provider: BaseProvider,
    protected gasLimitPerCall = 1_000_000
  ) {
    super()
    const multicallAddress = MULTICALL_ADDRESS[this.chainId]

    if (!multicallAddress) {
      throw new Error(`No address for Uniswap Multicall Contract on chain id: ${chainId}`)
    }

    this.multicallContract = UniswapInterfaceMulticall__factory.connect(multicallAddress, this.provider)
  }

  public async callSameFunctionOnMultipleContracts<TFunctionParams extends any[] | undefined, TReturn = any>(
    params: CallSameFunctionOnMultipleContractsParams<TFunctionParams>
  ): Promise<{
    blockNumber: BigNumber
    results: Result<TReturn>[]
  }> {
    const { addresses, contractInterface, functionName, functionParams, providerConfig } = params

    const blockNumberOverride = providerConfig?.blockNumber ?? undefined

    const fragment = contractInterface.getFunction(functionName)
    const callData = contractInterface.encodeFunctionData(fragment, functionParams)

    const calls = _.map(addresses, (address) => {
      return {
        target: address,
        callData,
        gasLimit: this.gasLimitPerCall,
      }
    })

    const { blockNumber, returnData: aggregateResults } = await this.multicallContract.callStatic.multicall(calls, {
      blockTag: blockNumberOverride,
    })

    const results: Result<TReturn>[] = []

    for (let i = 0; i < aggregateResults.length; i++) {
      const { success, returnData } = aggregateResults[i]!

      // Return data "0x" is sometimes returned for invalid calls.
      if (!success || returnData.length <= 2) {
        results.push({
          success: false,
          returnData,
        })
        continue
      }

      results.push({
        success: true,
        result: contractInterface.decodeFunctionResult(fragment, returnData) as unknown as TReturn,
      })
    }

    return { blockNumber, results }
  }

  public async callSameFunctionOnContractWithMultipleParams<TFunctionParams extends any[] | undefined, TReturn>(
    params: CallSameFunctionOnContractWithMultipleParams<TFunctionParams, UniswapMulticallConfig>
  ): Promise<{
    blockNumber: BigNumber
    results: Result<TReturn>[]
    approxGasUsedPerSuccessCall: number
  }> {
    const { address, contractInterface, functionName, functionParams, additionalConfig, providerConfig } = params
    const fragment = contractInterface.getFunction(functionName)

    const gasLimitPerCall = additionalConfig?.gasLimitPerCallOverride ?? this.gasLimitPerCall
    const blockNumberOverride = providerConfig?.blockNumber ?? undefined

    const calls = _.map(functionParams, (functionParam) => {
      const callData = contractInterface.encodeFunctionData(fragment, functionParam)

      return {
        target: address,
        callData,
        gasLimit: gasLimitPerCall,
      }
    })

    const { blockNumber, returnData: aggregateResults } = await this.multicallContract.callStatic.multicall(calls, {
      blockTag: blockNumberOverride,
    })

    const results: Result<TReturn>[] = []

    const gasUsedForSuccess: number[] = []
    for (let i = 0; i < aggregateResults.length; i++) {
      const { success, returnData, gasUsed } = aggregateResults[i]!

      // Return data "0x" is sometimes returned for invalid pools.
      if (!success || returnData.length <= 2) {
        results.push({
          success: false,
          returnData,
        })
        continue
      }

      gasUsedForSuccess.push(gasUsed.toNumber())

      results.push({
        success: true,
        result: contractInterface.decodeFunctionResult(fragment, returnData) as unknown as TReturn,
      })
    }

    return {
      blockNumber,
      results,
      approxGasUsedPerSuccessCall: stats.percentile(gasUsedForSuccess, 99),
    }
  }

  public async callMultipleFunctionsOnSameContract<TFunctionParams extends any[] | undefined, TReturn>(
    params: CallMultipleFunctionsOnSameContractParams<TFunctionParams, UniswapMulticallConfig>
  ): Promise<{
    blockNumber: BigNumber
    results: Result<TReturn>[]
    approxGasUsedPerSuccessCall: number
  }> {
    const { address, contractInterface, functionNames, functionParams, additionalConfig, providerConfig } = params

    const gasLimitPerCall = additionalConfig?.gasLimitPerCallOverride ?? this.gasLimitPerCall
    const blockNumberOverride = providerConfig?.blockNumber ?? undefined

    const calls = _.map(functionNames, (functionName, i) => {
      const fragment = contractInterface.getFunction(functionName)
      const param = functionParams ? functionParams[i] : []
      const callData = contractInterface.encodeFunctionData(fragment, param)
      return {
        target: address,
        callData,
        gasLimit: gasLimitPerCall,
      }
    })

    const { blockNumber, returnData: aggregateResults } = await this.multicallContract.callStatic.multicall(calls, {
      blockTag: blockNumberOverride,
    })

    const results: Result<TReturn>[] = []

    const gasUsedForSuccess: number[] = []
    for (let i = 0; i < aggregateResults.length; i++) {
      const fragment = contractInterface.getFunction(functionNames[i]!)
      const { success, returnData, gasUsed } = aggregateResults[i]!

      // Return data "0x" is sometimes returned for invalid pools.
      if (!success || returnData.length <= 2) {
        results.push({
          success: false,
          returnData,
        })
        continue
      }

      gasUsedForSuccess.push(gasUsed.toNumber())

      results.push({
        success: true,
        result: contractInterface.decodeFunctionResult(fragment, returnData) as unknown as TReturn,
      })
    }

    return {
      blockNumber,
      results,
      approxGasUsedPerSuccessCall: stats.percentile(gasUsedForSuccess, 99),
    }
  }
}
